import {usePush} from '../../libs/shared/core/src/services/push/pushContext';
import {
  useCanComposeCommentOffline,
  useCanComposeDiscussionOffline,
  useCanComposeMessageOffline,
  useCanDeleteDraftOffline,
  useCanShareDraftOffline,
  useCanTakeConversationSeenActionOffline,
  useCanTakeConversationStatusActionOffline,
} from '../../src/helpers/offlineAccessHelpers';
import {
  noPermission,
  PermissionDeniedReasonsEnum,
  PermissionResult,
  yesPermission,
} from '../../src/helpers/permissionHelpers';

/*
 * Generic hook.
 */

/**
 * Computes a permission from the current offline state.
 *
 * @param hasOfflinePermission
 *   Whether to grant permission when we are offline. Defaults to false.
 */
export function useOfflinePermission(canDoOffline = false): PermissionResult {
  const {isConnected} = usePush();
  return !isConnected && !canDoOffline ? noPermission(PermissionDeniedReasonsEnum.OFFLINE) : yesPermission();
}

/*
 * Specialized hooks.
 */

export const useComposeMessageOfflinePermission = makeUseOfflinePermission(useCanComposeMessageOffline);

export const useShareDraftOfflinePermission = makeUseOfflinePermission(useCanShareDraftOffline);

export const useComposeCommentOfflinePermission = makeUseOfflinePermission(useCanComposeCommentOffline);

export const useComposeDiscussionOfflinePermission = makeUseOfflinePermission(useCanComposeDiscussionOffline);

export const useConversationSeenActionOfflinePermission = makeUseOfflinePermission(
  useCanTakeConversationSeenActionOffline,
);

export const useConversationStatusActionOfflinePermission = makeUseOfflinePermission(
  useCanTakeConversationStatusActionOffline,
);

export const useDeleteDraftOfflinePermission = makeUseOfflinePermission(useCanDeleteDraftOffline);

/*
 * Helpers.
 */

function makeUseOfflinePermission<A extends Array<any>>(
  useCanDoOffline: (...args: A) => boolean,
): (...args: A) => PermissionResult {
  return (...args) => {
    const canDoOffline = useCanDoOffline(...args);
    const offlinePermission = useOfflinePermission(canDoOffline);

    return offlinePermission;
  };
}
